import _ from 'lodash';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { ApplicationState } from 'store/rootReducer';
import {
  AnalyticsDataState,
  HttpDataState,
  MsAnalyticsDataState,
  TabDataState,
} from 'store/analytics/data/analyticsDataTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { HttpRequest } from 'types/rest/analyticsTypes';
import {
  copyDataState,
  fetchAnalyticsInspectionDataDetail,
  fetchWebhooksInspectionData,
  isTabActive,
  updateCurrentDataContext,
  updateTabData,
} from 'store/analytics/data/analyticsDataActions';
import { action } from 'typesafe-actions';
import {
  WEBHOOK_VIEW_SET_IS_LOADING_DETAIL,
  WEBHOOK_VIEW_SET_IS_LOADING_REQUESTS,
  WEBHOOK_VIEW_SET_IS_REQUEST_SENT,
  WEBHOOK_VIEW_UPDATE,
} from 'store/analytics/data/analyticsDataActionTypes';
import { selectWebhookViewRequestForTab } from 'store/analytics/ui/analyticsUiActions';
import { BlazeMeterApiResponse } from '../../../types/rest/blazeMeterApiResponse';
import { selectWebhookViewTriggered } from '../ui/subViews/webhookView/webhookViewActions';
import { buildHttpDetail } from './analyticsHttpCallsActions';

const setWebhookDetailOutdated = (
  getState: any,
  tabId: number | string,
  dispatch: ThunkDispatch<ApplicationState, null, AnyAction>,
) => {
  const currentDataState: MsAnalyticsDataState = copyDataState(getState).activeDataContext;
  // Check if we are in same data context
  if (isTabActive(tabId)) {
    currentDataState.webhook.isLoadingDetails = false;
    currentDataState.webhook.isOutdated = true;
    dispatch(updateCurrentDataContext(currentDataState));
  }
};

/**
 * Updates inspection data in the current data context.
 * @param tabId
 * @param tabData
 */
export const updateCurrentWebhookViewData = (webhookViewData: HttpDataState) =>
  action(WEBHOOK_VIEW_UPDATE, webhookViewData);

export const setLoadingWebhooks = (isLoading: boolean) => action(WEBHOOK_VIEW_SET_IS_LOADING_REQUESTS, isLoading);
export const setWebhookRequestSent = (isWebhookRequested: boolean) =>
  action(WEBHOOK_VIEW_SET_IS_REQUEST_SENT, isWebhookRequested);

/**
 * Sets "IsLoading" webhook in details section.
 * @param isLoading
 */
export const setLoadingWebhookDetail = (isLoading: boolean) => action(WEBHOOK_VIEW_SET_IS_LOADING_DETAIL, isLoading);

/**
 * Select webhook in the request table. Marks the item in the table as selected and reloads
 * detail of the request.
 * @param requestId - id of the request to be loaded
 * @param tabId - id of the tab which requests belongs to
 */
export const fetchWebhookViewRequestDetailThunk = (
  webhookId: number,
  tabId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    //Do not continue if tab / request id is empty
    if (_.isNil(tabId) || _.isNil(webhookId)) {
      return;
    }

    //Add selected request to the ui state
    const tabData: TabDataState[] = getState().analytics.data.tabData;
    const tab = tabData.find((item: TabDataState) => String(item.tabId) === String(tabId));
    const httpRequest: HttpRequest = tab.webhook.httpRequests.find((webhook: any) => webhook.id === webhookId);

    if (isTabActive(tabId)) {
      await dispatch(selectWebhookViewTriggered(httpRequest));
      await dispatch(setLoadingWebhookDetail(true));
    } else {
      await dispatch(selectWebhookViewRequestForTab(tabId, httpRequest));
    }

    try {
      const response: BlazeMeterApiResponse = (await fetchAnalyticsInspectionDataDetail(webhookId)).data;
      const updatedTab = _.cloneDeep(tab);

      //Unwrap arguments from original webhook object, they are not in the detail
      updatedTab.webhook.detail = buildHttpDetail(httpRequest, response.result);
      updatedTab.webhook.isLoadingDetails = false;
      updatedTab.webhook.isOutdated = false;
      updatedTab.webhook.isDetailRequested = true;

      // Update data
      dispatch(updateTabData(updatedTab));

      //Is tab still active, update current inspection view
      if (isTabActive(tabId)) {
        dispatch(updateCurrentWebhookViewData(updatedTab.webhook));
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setWebhookDetailOutdated(getState, tabId, dispatch);
      } else {
        const errorBody = _.get(error, 'response.data.error', error.toString());
        dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve webhook detail data', errorBody));
      }
    }
  };
};

/**
 * We need to omit actions which were not triggered from the list.
 * @param inspectionData
 */
export const filterOnlyExecutedHttpInspectionData = (inspectionData: HttpRequest[]) => {
  return _.filter(inspectionData, item => item.actionExecuted !== false);
};

/**
 * Gets requests for inspection view. Results are stored in current data context. If data context changed in the meanwhile,
 * they are stored to the correct one.
 */
export const fetchWebhooksListThunk = (
  mockServiceId: number | string | null,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>, getState: any): Promise<void> => {
    if (_.isNil(mockServiceId)) {
      console.error('No virtual service id available.');
      return;
    }

    //Set loading or requests to true in current inspection view
    await dispatch(setWebhookRequestSent(true));
    await dispatch(setLoadingWebhooks(true));
    const dataState: AnalyticsDataState = copyDataState(getState);

    try {
      const response: BlazeMeterApiResponse = (await fetchWebhooksInspectionData(mockServiceId)).data;

      // Process data, update current inspection view
      const requests = filterOnlyExecutedHttpInspectionData(response.result);

      const activeDataContext = dataState.activeDataContext as MsAnalyticsDataState;
      activeDataContext.webhook.httpRequests = requests;
      activeDataContext.webhook.isLoadingData = false;
      activeDataContext.webhook.lastRefreshTs = new Date().getTime();

      // Save result to data array
      dispatch(updateTabData(activeDataContext));

      // Update current data context (if still active)
      if (isTabActive(mockServiceId)) {
        await dispatch(updateCurrentWebhookViewData(activeDataContext.webhook));
      }

      //Preselect first item and do the reload of details
      if (requests.length !== 0) {
        const webhookId = parseInt(requests[0].id);
        dispatch(fetchWebhookViewRequestDetailThunk(webhookId, mockServiceId));
      }
    } catch (error) {
      const errorBody = _.get(error, 'response.data.error', error.toString());
      dispatch(showToastNotification(ToastType.Danger, 'Cannot retrieve analytics data', errorBody));
      if (isTabActive(mockServiceId)) {
        dispatch(setLoadingWebhooks(false));
      }
    }
  };
};
